import { MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { menuProps } from '~/components/toolbar/OperationsToolbar/selectMenuProps';
import {
    AVAILABLE_ROI_TYPES,
    ROI_POINT_TYPES,
    RoiInterpretedType,
} from '~/domain/dicom/dicomstructureset';

interface InterpretedTypeSelectProps {
    value: RoiInterpretedType;
    onChange: (value: RoiInterpretedType) => void;
    /**
     * Name of the structure set's existing EXTERNAL, when it has one.
     * EXTERNAL is then offered disabled, with the holder named.
     */
    existingExternalName?: string;
    /** Test id, stamped on the select root. */
    dataTestId?: string;
}

/**
 * Interpreted-type picker for a structure that does not exist yet: the
 * volumetric DICOM types, with UNKNOWN leading the menu so the untyped
 * choice stays visible and re-selectable.
 *
 * A structure set may hold only one EXTERNAL. The invariant itself lives
 * in the domain ({@link DuplicateExternalRoiError} from
 * DicomStructureSet.createRoi), so nothing here can bypass it; this
 * control only mirrors it, offering a taken EXTERNAL disabled with the
 * reason instead of letting the choice surface as a thrown error. The
 * reason is a caption under the control rather than a tooltip on the
 * item: a disabled item takes no pointer events, so a hint that needs
 * hover is a hint nobody reads.
 *
 * The type of an EXISTING structure is edited through
 * {@link StructureTypeMenu}, which reads the ROI it belongs to.
 */
export default function InterpretedTypeSelect({
    value,
    onChange,
    existingExternalName,
    dataTestId,
}: InterpretedTypeSelectProps) {
    const intl = useIntl();

    const availableTypes = useMemo(
        () => [
            RoiInterpretedType.UNKNOWN,
            ...AVAILABLE_ROI_TYPES.filter(
                (type) => !ROI_POINT_TYPES.includes(type)
            ),
        ],
        []
    );

    return (
        <>
            <Select
                value={value}
                onChange={(e: SelectChangeEvent) =>
                    onChange(
                        e.target
                            .value as RoiInterpretedType /* menu values are enum-sourced */
                    )
                }
                size="small"
                fullWidth
                sx={{ fontSize: 12 }}
                MenuProps={menuProps}
                data-testid={dataTestId}
                // UNKNOWN is the empty string, which MUI treats as
                // "nothing selected" — without this the closed control
                // renders blank instead of the matched "Unknown" item.
                displayEmpty
            >
                {/* Every item is a DIRECT child of the Select: it clones
                    role and selection state onto its own children only,
                    so an item behind a wrapper drops out of the listbox's
                    option set. */}
                {availableTypes.map((type) => (
                    <MenuItem
                        key={type || 'UNKNOWN'}
                        value={type}
                        disabled={
                            type === RoiInterpretedType.EXTERNAL &&
                            existingExternalName !== undefined
                        }
                        sx={{ fontSize: 12 }}
                    >
                        {type || intl.formatMessage({ id: 'unknown' })}
                    </MenuItem>
                ))}
            </Select>
            {existingExternalName !== undefined && (
                <Typography fontSize={10} color="text.secondary">
                    <FormattedMessage
                        id="structure.externalTaken"
                        values={{ name: existingExternalName }}
                    />
                </Typography>
            )}
        </>
    );
}
